import { useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'

interface VideoPlayerProps {
    playlist: string
    thumbnail?: string
    aspectRatio?: { width: number; height: number }
    alt?: string
}

export function VideoPlayer({ playlist, thumbnail, aspectRatio, alt }: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const hlsRef = useRef<Hls | null>(null)

    useEffect(() => {
        return () => {
            hlsRef.current?.destroy()
        }
    }, [])

    const startPlayback = async () => {
        const video = videoRef.current
        if (!video) return

        // Direct MP4/WebM/MOV — native HTML5 video, no HLS needed
        if (/\.(mp4|webm|mov)(\?.*)?$/i.test(playlist)) {
            setIsPlaying(true)
            video.src = playlist
            try {
                await video.play()
            } catch {
                setIsPlaying(false)
            }
            return
        }

        // HLS stream — Safari native
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            setIsPlaying(true)
            video.src = playlist
            try {
                await video.play()
            } catch {
                setIsPlaying(false)
            }
            return
        }

        // HLS stream — Chrome/Firefox via hls.js
        const { default: HlsLib } = await import('hls.js')
        if (!HlsLib.isSupported()) return

        const hls = new HlsLib()
        hlsRef.current = hls
        hls.loadSource(playlist)
        hls.attachMedia(video)
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
            setIsPlaying(true)
            video.play().catch(() => setIsPlaying(false))
        })
        hls.on(HlsLib.Events.ERROR, (_event, data) => {
            if (data.fatal) setIsPlaying(false)
        })
    }

    const paddingTop = aspectRatio ? `${(aspectRatio.height / aspectRatio.width) * 100}%` : '56.25%' // Default 16:9

    return (
        <div
            className="relative rounded-lg overflow-hidden border border-border-light dark:border-border-dark bg-black"
            style={{ paddingTop }}
            onClick={e => e.stopPropagation()}
        >
            <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-contain"
                poster={thumbnail}
                controls={isPlaying}
                playsInline
                loop
                aria-label={alt}
            />
            {!isPlaying && (
                <button
                    onClick={startPlayback}
                    className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                    aria-label="Play video"
                >
                    {thumbnail && (
                        <img
                            src={thumbnail}
                            alt=""
                            className="absolute inset-0 w-full h-full object-contain"
                            onError={e => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    )}
                    <div className="relative z-10 w-14 h-14 rounded-full bg-black/60 flex items-center justify-center group-hover:bg-black/80 transition-colors">
                        <svg
                            className="w-7 h-7 text-white ml-1"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </div>
                </button>
            )}
        </div>
    )
}
